import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { MessageSquare, Send, Loader2, DollarSign, CheckCircle, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const InboxPage = () => {
  const { conversationId: routeConvId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isArtist, loading: authLoading } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  // Offer state (artist side)
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerAmount, setOfferAmount] = useState('500');
  const [offerNotes, setOfferNotes] = useState('');
  const [creatingOffer, setCreatingOffer] = useState(false);

  // Accept&Pay state (buyer side)
  const [payingOfferId, setPayingOfferId] = useState(null);

  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/conversations`);
      setConversations(res.data);
      return res.data;
    } catch (e) {
      console.error(e);
      toast.error('Failed to load inbox');
      return [];
    }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/conversations/${convId}/messages`);
      setMessages(res.data.messages);
      setActiveConv(res.data.conversation);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load conversation');
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    (async () => {
      const convs = await fetchConversations();
      if (routeConvId) {
        await fetchMessages(routeConvId);
      } else if (convs.length > 0) {
        await fetchMessages(convs[0].id);
        navigate(`/inbox/${convs[0].id}`, { replace: true });
      }
      setLoading(false);
    })();
  }, [authLoading, isAuthenticated, routeConvId, fetchConversations, fetchMessages, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !activeConv) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/conversations/${activeConv.id}/messages`, { body });
      setDraft('');
      await fetchMessages(activeConv.id);
      await fetchConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCreateOffer = async (e) => {
    e.preventDefault();
    setCreatingOffer(true);
    try {
      await axios.post(`${BACKEND_URL}/api/exclusive-offers`, {
        conversation_id: activeConv.id,
        amount: parseFloat(offerAmount),
        notes: offerNotes,
      });
      toast.success('Offer sent');
      setOfferOpen(false);
      setOfferNotes('');
      await fetchMessages(activeConv.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create offer');
    } finally {
      setCreatingOffer(false);
    }
  };

  const handleAcceptAndPay = async (offerId) => {
    setPayingOfferId(offerId);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/exclusive-offers/${offerId}/accept-and-pay`,
        { origin_url: window.location.origin }
      );
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setPayingOfferId(null);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const isArtistInThisConv = activeConv && activeConv.artist_id === user?.id;
  const isBuyerInThisConv = activeConv && activeConv.buyer_id === user?.id;

  return (
    <div className="min-h-screen bg-gradient-dark pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-2 block">
            Messages
          </span>
          <h1 className="text-3xl font-medium text-white">Inbox</h1>
          <p className="text-slate-400 mt-2 text-sm">Negotiate exclusive rights and chat with artists & buyers.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 min-h-[60vh]">
          {/* Conversations list */}
          <aside className="bg-slate-900/50 border border-white/10 rounded-xl overflow-hidden" data-testid="inbox-conversations">
            {conversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No conversations yet.</p>
                <p className="text-xs text-slate-500 mt-1">Start one from any track page.</p>
              </div>
            ) : (
              <ul>
                {conversations.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => { navigate(`/inbox/${c.id}`); fetchMessages(c.id); }}
                      className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${activeConv?.id === c.id ? 'bg-white/5' : ''}`}
                      data-testid={`conv-${c.id}`}
                    >
                      <p className="text-sm font-medium text-white truncate">{c.track_title || 'Direct message'}</p>
                      <p className="text-xs text-slate-400 truncate">{c.other_name || 'Unknown'} · {c.other_role}</p>
                      {c.last_message_preview && (
                        <p className="text-xs text-slate-500 truncate mt-1">{c.last_message_preview}</p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Active conversation */}
          <main className="bg-slate-900/50 border border-white/10 rounded-xl flex flex-col" data-testid="inbox-thread">
            {!activeConv ? (
              <div className="flex-1 flex items-center justify-center p-12 text-center">
                <div>
                  <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">Select a conversation to start chatting</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-white truncate" data-testid="active-conv-track">
                      {activeConv.track_title || 'Direct message'}
                    </h2>
                    <p className="text-xs text-slate-400">with {activeConv.other_name || 'participant'}</p>
                  </div>
                  {isArtistInThisConv && activeConv.track_id && (
                    <Button
                      onClick={() => setOfferOpen(true)}
                      className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold rounded-lg"
                      data-testid="make-offer-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Make Exclusive Offer
                    </Button>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                  {messages.length === 0 ? (
                    <p className="text-center text-sm text-slate-500 py-8">No messages yet — say hi.</p>
                  ) : messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    if (m.type === 'system') {
                      return (
                        <div key={m.id} className="text-center my-2">
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/10 text-emerald-300 text-xs rounded-full border border-emerald-500/20">
                            <CheckCircle className="w-3 h-3" /> {m.body}
                          </span>
                        </div>
                      );
                    }
                    if (m.type === 'offer') {
                      const showAcceptBtn = isBuyerInThisConv && m.offer_id;
                      return (
                        <div key={m.id} className="flex justify-center my-3" data-testid={`offer-msg-${m.offer_id || m.id}`}>
                          <div className="max-w-sm w-full p-4 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl">
                            <div className="flex items-center gap-2 text-emerald-300 mb-2">
                              <DollarSign className="w-4 h-4" />
                              <span className="text-xs font-bold uppercase tracking-wide">Exclusive Offer</span>
                            </div>
                            <p className="text-white text-sm">{m.body}</p>
                            {showAcceptBtn && (
                              <Button
                                onClick={() => handleAcceptAndPay(m.offer_id)}
                                disabled={payingOfferId === m.offer_id}
                                className="mt-3 w-full bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold rounded-lg"
                                data-testid={`accept-pay-${m.offer_id}`}
                              >
                                {payingOfferId === m.offer_id ? (
                                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting...</>
                                ) : 'Accept & Pay'}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] px-4 py-2 rounded-2xl ${mine ? 'bg-emerald-500 text-slate-900 rounded-br-sm' : 'bg-slate-800 text-white rounded-bl-sm'}`}>
                          <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Composer */}
                <form onSubmit={handleSend} className="p-3 border-t border-white/10 flex gap-2" data-testid="composer">
                  <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-slate-800 border-white/10 text-white rounded-lg"
                    data-testid="composer-input"
                  />
                  <Button type="submit" disabled={sending || !draft.trim()} className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 rounded-lg" data-testid="composer-send">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
              </>
            )}
          </main>
        </div>
      </div>

      {/* Make Offer Dialog */}
      <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Send Exclusive Rights Offer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOffer} className="space-y-4 mt-2" data-testid="offer-form">
            <p className="text-sm text-slate-400">
              The buyer will see this offer in the chat and can pay through Stripe.
              A contract is auto-issued on successful payment.
            </p>
            <div className="space-y-2">
              <Label className="text-slate-300">Amount (USD)</Label>
              <Input
                type="number"
                min="1"
                step="0.01"
                value={offerAmount}
                onChange={(e) => setOfferAmount(e.target.value)}
                required
                className="bg-slate-800 border-white/10 text-white"
                data-testid="offer-amount"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Notes (optional)</Label>
              <Textarea
                value={offerNotes}
                onChange={(e) => setOfferNotes(e.target.value)}
                placeholder="e.g. All rights transferred worldwide & perpetual."
                className="bg-slate-800 border-white/10 text-white"
                data-testid="offer-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOfferOpen(false)} className="border-white/10 text-slate-300 hover:bg-white/5">
                Cancel
              </Button>
              <Button type="submit" disabled={creatingOffer || !offerAmount} className="bg-emerald-500 text-slate-900 hover:bg-emerald-400" data-testid="send-offer-btn">
                {creatingOffer ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Offer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
